import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@mogenius/environments/environment';
import { BaseService } from '@mogenius/shared/services/base-service';
import { BehaviorSubject, Observable, tap, catchError } from 'rxjs';
import { DashboardResponseDto } from '@mogenius/client-sdk/core-dto';

@Injectable({
  providedIn: 'root'
})
export class DashboardService extends BaseService {
  protected readonly _httpClient = inject(HttpClient);

  protected _apiBase: any = environment.moProduct.dashboard;

  private _dashboardData$ = new BehaviorSubject<DashboardResponseDto | null>(null);
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _error$ = new BehaviorSubject<string | null>(null);

  // Fetch dashboard data from the aggregation endpoint
  public loadDashboard(hideLoadingSpinner: boolean = true): Observable<DashboardResponseDto> {
    this._loading$.next(true);
    this._error$.next(null);

    const endpoint = this.getEndpoint(this._apiBase.get);

    return this._httpClient
      .request<DashboardResponseDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header.contentType,
          hideLoadingSpinner: hideLoadingSpinner ? '1' : '0'
        }
      })
      .pipe(
        tap((response: DashboardResponseDto) => {
          this._dashboardData$.next(response);
          this._loading$.next(false);
        }),
        catchError((err) => {
          this._error$.next(err?.message || 'Failed to load dashboard data');
          this._loading$.next(false);
          throw err;
        })
      );
  }

  // Force refresh the dashboard data
  public refresh(): Observable<DashboardResponseDto> {
    return this.loadDashboard(true);
  }

  // Reset state on cleanup
  public cleanUp(): void {
    this._dashboardData$.next(null);
    this._loading$.next(false);
    this._error$.next(null);
  }

  get dashboardData$(): Observable<DashboardResponseDto | null> {
    return this._dashboardData$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this._loading$.asObservable();
  }

  get error$(): Observable<string | null> {
    return this._error$.asObservable();
  }

  get currentData(): DashboardResponseDto | null {
    return this._dashboardData$.value;
  }
}
